/**
 * `SiteMachineIcon` — one machine on a site card. Port of the demo's `mIcon(f)`
 * (line 2020) with its `.mi` recipe (lines 401–403 and 1016).
 *
 * A small class silhouette (or rendered icon, below) tinted by drive type, with a dot in the corner
 * coloured by the machine's condition band. The demo's glyphs (`ICON`, line
 * 2014) are copied path for path; their wheels were a literal near-black, which
 * here reads the tertiary-ink token so they stay visible in dark mode.
 *
 * Drive colours follow `modules/fleet/MachineCard.tsx`'s `DRIVE_EDGE`, so a
 * machine's accent is the same on its site card and on its fleet card.
 *
 * Where the model has turntable frames the glyph gives way to its rendered
 * icon, as the demo's `turnIcon(f)` did (`.mi.pic`, 54 × 30): lane L7's
 * `MachineThumb`, in the brand livery. The glyph stays for any model without
 * frames.
 *
 * UI layer — every figure arrives derived from `SitesMapScene.transform.ts`.
 */

import { Link } from 'react-router-dom';
import { BY_ID } from '../../data/core';
import type { MachineClass } from '../../schema/machine';
import { hasTurntable, MachineThumb } from '../../shared/viz/Turntable';
import type { DriveKey, SiteCardMachine } from './SitesMapScene.transform';

/** Same mapping as `MachineCard`'s `DRIVE_EDGE`; see that file for the reasoning. */
export const DRIVE_COLOUR: Readonly<Record<DriveKey, string>> = {
  diesel: 'var(--accent)',
  batt: 'var(--band-good)',
  auto: 'var(--state-info)',
};

const WHEEL = 'var(--ink-tertiary)';

/** Demo line 2014. `ICON` — one 26 × 14 silhouette per machine class. */
function Glyph({ cls }: { cls: MachineClass }) {
  switch (cls) {
    case 'sdrill':
      return (
        <>
          <path d="M2 12h14v2H2z" fill="currentColor" />
          <rect x="18" y="1" width="3" height="12" fill="currentColor" />
          <path d="M4 8h8v4H4z" fill="currentColor" />
          <path d="M1 14h16" stroke={WHEEL} strokeWidth={1.4} />
        </>
      );
    case 'jumbo':
      return (
        <>
          <path d="M14 9h9v3h-9zM2 8h11l-1 4H2z" fill="currentColor" />
          <path d="M1 5h7M1 7h5" stroke="currentColor" strokeWidth={1.2} />
          <circle cx="16" cy="12.5" r="1.6" fill={WHEEL} />
          <circle cx="22" cy="12.5" r="1.6" fill={WHEEL} />
        </>
      );
    case 'longhole':
      return (
        <>
          <path d="M9 8h13v4H9z" fill="currentColor" />
          <rect x="3" y="1" width="2" height="11" fill="currentColor" />
          <circle cx="12" cy="12.5" r="1.6" fill={WHEEL} />
          <circle cx="19" cy="12.5" r="1.6" fill={WHEEL} />
        </>
      );
    case 'lhd':
      return (
        <>
          <path d="M12 6h12v6H12zM3 6h6v6H3z" fill="currentColor" />
          <path d="M1 3h4l1 7H1z" fill="currentColor" />
          <circle cx="8" cy="12.5" r="1.7" fill={WHEEL} />
          <circle cx="19" cy="12.5" r="1.7" fill={WHEEL} />
        </>
      );
    case 'truck':
      return (
        <>
          <path d="M2 6h6v6H2z" fill="currentColor" />
          <path d="M9 5l15-1v8H9z" fill="currentColor" />
          <circle cx="6" cy="12.5" r="1.7" fill={WHEEL} />
          <circle cx="19" cy="12.5" r="1.7" fill={WHEEL} />
        </>
      );
  }
}

/** Demo line 2020. A linked icon: tooltip and accessible name are the demo's `title`. */
export function SiteMachineIcon({ machine }: { machine: SiteCardMachine }) {
  // Demo line 2020: the model's turntable icon (`.mi.pic`, 54 × 30) where one
  // exists, else the class glyph (`.mi`, 34 × 22).
  const f = BY_ID[machine.id];
  const pic = f != null && hasTurntable(f.model);
  return (
    <Link
      to={machine.href}
      title={machine.title}
      aria-label={machine.title}
      className={`relative inline-grid place-items-center rounded border border-[var(--hairline)] bg-[var(--surface-2)] transition-colors hover:border-[var(--hairline-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] ${pic ? 'h-[30px] w-[54px] p-px' : 'h-[22px] w-[34px]'}`}
      style={{ color: DRIVE_COLOUR[machine.drive] }}
    >
      {pic ? (
        <MachineThumb machine={f} className="h-full w-full" />
      ) : (
        <svg viewBox="0 0 26 14" width={26} height={14} aria-hidden="true">
          <Glyph cls={machine.cls} />
        </svg>
      )}
      <i
        aria-hidden="true"
        className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full"
        style={{ background: `var(--band-${machine.band})` }}
      />
    </Link>
  );
}
